import React from 'react';
import Box from '@nubank/nuds-web/components/Box/Box';
import Typography from '@nubank/nuds-web/components/Typography/Typography';
import Image from '@nubank/nuds-web/components/Image/Image';
import Link from '@nubank/nuds-web/components/Link/Link';
import styled from 'styled-components';
import { useParallax } from 'react-scroll-parallax';

// Description is a intlKey because it needs to add intlValues
const benefits = [
  {
    title: 'Mastercard Global Service',
    description: 'BENEFITS.MASTERCARD.GLOBAL.DESCRIPTION',
    externalLink: 'https://www.mastercard.com.mx/es-mx/consumidores/encontrar-una-tarjeta/tarjetas-de-credito/tarjeta-gold/asistencia.html',
  },
  {
    title: 'Garantía Extendida',
    description: 'BENEFITS.MASTERCARD.WARANTY.DESCRIPTION',
    externalLink: 'https://www.mastercard.com.mx/es-mx/consumidores/encontrar-una-tarjeta/tarjetas-de-credito/tarjeta-gold/garantia-extendida.html',
  },
  {
    title: 'Protección de Compras',
    description: 'BENEFITS.MASTERCARD.PROTECTION.DESCRIPTION',
    externalLink: 'https://www.mastercard.com.mx/es-mx/consumidores/encontrar-una-tarjeta/tarjetas-de-credito/tarjeta-gold/proteccion-compras.html',
  },
  {
    title: 'Priceless Cities',
    description: 'BENEFITS.MASTERCARD.CITY.DESCRIPTION',
    externalLink: 'https://www.priceless.com/',
  },
];

const StyledTypography = styled(Typography)`
  background: linear-gradient(99.92deg, #9f2deb 18.57%, #6200a3 71.77%);
  /* stylelint-disable property-no-vendor-prefix, property-no-unknown */
  -webkit-text-fill-color: transparent;
  -webkit-background-clip: text;
  background-clip: text;
`;

const StyledImagesBox = styled(Box)`
  gap: 16px;
  margin: 0 auto;
`;

const Mastercard = () => {
  const { ref } = useParallax({ speed: 30, easing: [0, 1, 0.9, 0.2] });

  return (
    <Box
      tag="section"
      display="flex"
      flexDirection="column"
      marginTop={{ xs: '94px', md: '116px' }}
      marginBottom={{ xs: '94px', md: '116px' }}
      alignItems="center"
    >
      <Box
        width="100%"
        paddingHorizontal={{ xs: '0', lg: '8vw', xl: '16vw' }}
      >

        {/* IMAGES BOX */}

        <StyledImagesBox
          ref={ref}
          display="flex"
          justifyContent="center"
          maxWidth={{ xs: '100%', md: '1391px' }}
          height={{ xs: 'auto', md: '252px' }}
        >
          <Image
            alt="Mano sosteniendo una tarjeta morada Mastercard, lista para realizar pagos y transacciones seguras."
            srcSet={{
              xs: 'benefits/mastercard-left-xs.jpg',
              md: 'benefits/mastercard-left-lg.jpg',
            }}
            height={{ xs: '100%', md: '252px' }}
            width="100%"
            maxWidth={{ xs: '100%', md: '685px' }}
            webp
            loading="lazy"
          />
          <Image
            alt="Mano sosteniendo una tarjeta morada Mastercard, lista para realizar pagos y transacciones seguras."
            srcSet={{
              xs: 'benefits/mastercard-right-xs.jpg',
              md: 'benefits/mastercard-right-lg.jpg',
            }}
            height={{ xs: '100%', md: '252px' }}
            width="100%"
            maxWidth={{ xs: '100%', md: '685px' }}
            webp
            loading="lazy"
          />
        </StyledImagesBox>

        {/* CONTENT */}

        <Box
          maxWidth={{ xs: '100%', md: '802px' }}
          display="flex"
          flexDirection="column"
          alignItems="center"
          margin="0 auto"
          marginTop={{ xs: '-100px', md: '-110px' }}
          paddingHorizontal={{ xs: '32px', md: '0' }}
        >
          {/* MAIN GRAY BOX  WITH TITLE AND ICON */}

          <Box
            width="100%"
            backgroundColor="#F8F8FA"
            borderRadius="12px"
            paddingHorizontal={{ xs: '29px', md: '153px' }}
            paddingBottom={{ xs: '42px', md: '54px' }}
            marginHorizontal={{ xs: '8x', lg: '0' }}
            position="relative"
            display="flex"
            flexDirection="column"
          >
            <Box
              display="flex"
              justifyContent={{ xs: 'start', md: 'center' }}
              marginTop={{ xs: '-40px', md: '-50px' }}
            >

              <Image
                alt="Icono de un candado"
                src="benefits/card-icon-new.svg"
                webp
                loading="lazy"
              />
            </Box>

            <Typography
              variant="heading3"
              intlKey="BENEFITS.MASTERCARD.TITLE"
              paddingTop={{ xs: '4x', md: '32px' }}
              intlValues={{
                highlight: (
                  <StyledTypography
                    tag="span"
                    intlKey="BENEFITS.MASTERCARD.TITLE.HIGHLIGHT_BANK"
                    variant="heading3"
                    color="primary"
                  />
                ),
                highlight2: (
                  <StyledTypography
                    tag="span"
                    intlKey="BENEFITS.MASTERCARD.TITLE.HIGHLIGHT.TWO"
                    variant="heading3"
                    color="primary"
                  />
                ),
              }}
              whiteSpace="pre-line"
              textAlign={{ xs: 'left', md: 'center' }}
            />
          </Box>

          {/* OTHER GRAY BOXES */}

          {benefits.map(benefit => (
            <Box
              width="100%"
              backgroundColor="#F8F8FA"
              borderRadius="12px"
              paddingVertical="6x"
              marginTop="4x"
              marginHorizontal={{ xs: '8x', lg: '0' }}
              paddingHorizontal={{ xs: '29px', md: '140px' }}
              height={{ xs: 'auto', md: '167px' }}
              display="flex"
              flexDirection="column"
              justifyContent="center"
            >
              <Typography
                variant="subtitle2"
                strong
                marginBottom="4x"
                textAlign={{ xs: 'left', md: 'center' }}
              >
                {benefit.title}
              </Typography>

              <Typography
                variant="paragraph1"
                intlKey={benefit.description}
                margin="0 auto"
                intlValues={{
                  clickHereLink: (
                    <Link
                      href={benefit.externalLink}
                      color="primary"
                      typographyProps={{ variant: 'paragraph1' }}
                    >
                      haz clic aquí
                    </Link>
                  ),
                }}
                textAlign={{ xs: 'left', md: 'center' }}
              />
            </Box>
          ))}
        </Box>

      </Box>
    </Box>

  );
};

export default Mastercard;
